import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import config from '../../config/config';
import DataTable from 'react-data-table-component';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { FaHistory, FaEye, FaSearch, FaFilter, FaTimes, FaFileAlt, FaCheckCircle, FaExclamationCircle, FaCalendarAlt } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import { getPublikatorMenuItems } from '../../utils/menuItems';
import '../../styles/App.css';
import '../../styles/HistoryDataPage.css';

const HistoryDataPage = () => {
  const tableRef = useRef(null);
  const [historyData, setHistoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [igtList, setIgtList] = useState([]);
  const [selectedIgt, setSelectedIgt] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    setDataLoaded(false);
    try {
      const token = localStorage.getItem('token');
      const decoded = jwtDecode(token);
      
      if (!token || !decoded.twoFactorVerified) {
        navigate('/');
        return;
      }

      // Fetch history data
      const response = await axios.get(`${config.API_URL}/publikator/history`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      setHistoryData(response.data);
      setFilteredData(response.data);

      // Fetch IGT list for filter
      const igtResponse = await axios.get(`${config.API_URL}/publikator/history/igt-list`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setIgtList(igtResponse.data);
      setDataLoaded(true);

    } catch (err) {
      console.error('Error fetching history data:', err);
      setError('Gagal memuat data history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [navigate]);

  // Apply filters only when data is loaded and filters change
  useEffect(() => {
    if (!dataLoaded || historyData.length === 0) return;
    
    let filtered = [...historyData];

    if (searchText) {
      const query = searchText.toLowerCase();
      filtered = filtered.filter((row) =>
        row.no_tiket?.toLowerCase().includes(query) ||
        row.nama_igt?.toLowerCase().includes(query) ||
        row.kl?.toLowerCase().includes(query) ||
        row.catatan?.toLowerCase().includes(query)
      );
    }

    if (selectedIgt) {
      filtered = filtered.filter(row => row.nama_igt === selectedIgt);
    }

    if (statusFilter) {
      filtered = filtered.filter(row => row.status === statusFilter);
    }

    setFilteredData(filtered);
  }, [searchText, selectedIgt, statusFilter, historyData, dataLoaded]);

  const handleSearch = () => {
    // Trigger re-filter (the useEffect above handles the actual filtering)
    setSearchLoading(true);
    setTimeout(() => setSearchLoading(false), 300);
  };

  const clearFilters = () => {
    setSearchText('');
    setSelectedIgt('');
    setStatusFilter('');
    setFilteredData(historyData);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      // Status Tiket (dari datahistory.status)
      'Belum Diverifikasi': { bg: '#6b7280', color: '#fff', icon: <FaFileAlt /> },
      'Sedang Verifikasi': { bg: '#8b5cf6', color: '#fff', icon: <FaFileAlt /> },
      'Perlu Perbaikan': { bg: '#ef4444', color: '#fff', icon: <FaExclamationCircle /> },
      'Sudah Terintegrasi': { bg: '#3b82f6', color: '#fff', icon: <FaCheckCircle /> },
      'Revisi Pengajuan': { bg: '#f97316', color: '#fff', icon: <FaExclamationCircle /> },
      // Status Publikasi
      'Belum Publikasi': { bg: '#9ca3af', color: '#fff', icon: <FaFileAlt /> },
      'Siap Publikasi': { bg: '#eab308', color: '#fff', icon: <FaFileAlt /> },
      'Sudah Publikasi': { bg: '#10b981', color: '#fff', icon: <FaCheckCircle /> },
      'Batal Publikasi': { bg: '#f59e0b', color: '#fff', icon: <FaExclamationCircle /> },
      // Status dari history
      'Hasil Verifikasi': { bg: '#6366f1', color: '#fff', icon: <FaCheckCircle /> },
      'Update Data': { bg: '#0ea5e9', color: '#fff', icon: <FaFileAlt /> },
      'Upload': { bg: '#22c55e', color: '#fff', icon: <FaFileAlt /> }
    };

    const style = statusColors[status] || { bg: '#94a3b8', color: '#fff', icon: <FaFileAlt /> };
    
    return (
      <span className="history-status-badge" style={{ backgroundColor: style.bg, color: style.color }}>
        {style.icon} {status || '-'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      name: 'No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '60px',
      cell: (row, index) => <div style={{ fontWeight: '500' }}>{index + 1}</div>
    },
    {
      name: 'Tanggal',
      selector: row => row.tanggal ? new Date(row.tanggal) : new Date(0),
      sortable: true,
      minWidth: '160px',
      cell: row => (
        <div className="history-date-cell">
          <FaCalendarAlt className="history-date-icon" />
          {formatDate(row.tanggal)}
        </div>
      )
    },
    {
      name: 'No Tiket',
      selector: row => row.no_tiket,
      sortable: true,
      minWidth: '130px',
      cell: row => (
        <Link 
          to={`/rekapdata/${btoa(row.no_tiket)}`}
          className="history-tiket-link"
        >
          {row.no_tiket}
        </Link>
      )
    },
    {
      name: 'Nama IGT',
      selector: row => row.nama_igt,
      sortable: true,
      minWidth: '200px',
      cell: row => (
        <div data-tooltip-id="nama-tooltip" data-tooltip-content={row.nama_igt}>
          {row.nama_igt?.length > 40 ? `${row.nama_igt.substring(0, 40)}...` : row.nama_igt}
        </div>
      )
    },
    {
      name: 'KL',
      selector: row => row.kl,
      sortable: true,
      minWidth: '150px',
      cell: row => (
        <div data-tooltip-id="kl-tooltip" data-tooltip-content={row.kl}>
          {row.kl?.length > 25 ? `${row.kl.substring(0, 25)}...` : row.kl}
        </div>
      )
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      minWidth: '150px',
      cell: row => getStatusBadge(row.status)
    },
    {
      name: 'Catatan/Keterangan',
      selector: row => row.catatan,
      sortable: false,
      minWidth: '250px',
      cell: row => (
        <div 
          className="history-catatan-cell"
          data-tooltip-id="catatan-tooltip" 
          data-tooltip-content={row.catatan || '-'}
        >
          {row.catatan?.length > 50 ? `${row.catatan.substring(0, 50)}...` : row.catatan || '-'}
        </div>
      )
    },
    {
      name: 'Oleh',
      selector: row => row.fullname,
      sortable: true,
      minWidth: '130px',
      cell: row => row.fullname || '-'
    },
    {
      name: 'Sumber',
      selector: row => row.source,
      sortable: true,
      minWidth: '100px',
      cell: row => (
        <span className={`history-source-badge ${row.source === 'datahistory' ? 'source-data' : 'source-catatan'}`}>
          {row.source === 'datahistory' ? 'Status' : 'Catatan'}
        </span>
      )
    },
    {
      name: 'Aksi',
      cell: row => (
        <Link 
          to={`/rekapdata/${btoa(row.no_tiket)}`}
          className="history-action-button"
          data-tooltip-id="action-tooltip"
          data-tooltip-content="Lihat Detail Data"
        >
          <FaEye /> Detail
        </Link>
      ),
      minWidth: '100px',
    }
  ];

  const conditionalRowStyles = [
    {
      when: row => row.status === 'Sudah Publikasi',
      style: {
        backgroundColor: '#f0fdf4',
        borderLeft: '4px solid #10b981',
      },
    },
    {
      when: row => row.status === 'Sudah Terintegrasi',
      style: {
        backgroundColor: '#eff6ff',
        borderLeft: '4px solid #3b82f6',
      },
    },
    {
      when: row => row.status === 'Perlu Perbaikan' || row.status === 'Batal Publikasi',
      style: {
        backgroundColor: '#fef2f2',
        borderLeft: '4px solid #ef4444',
      },
    },
  ];

  // Get unique statuses for filter
  const uniqueStatuses = [...new Set(historyData.map(item => item.status).filter(Boolean))];

  const menuItems = getPublikatorMenuItems();

  return (
    <DashboardLayout menuItems={menuItems} userRole="Publikator">
      <div className="history-container">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/publikator">Dashboard</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              History Data
            </li>
          </ol>
        </nav>

        <div className="history-header">
          <div className="history-title">
            <FaHistory className="history-title-icon" />
            <h2>History Perubahan Data IGT</h2>
          </div>
          <p className="history-subtitle">
            Riwayat semua perubahan status dan catatan pada data IGT
          </p>
        </div>

        <div className="history-filter-section">
          <div className="history-search-row">
            <div className="history-search-container">
              <FaSearch className="history-search-icon" />
              <input
                type="text"
                className="history-search-input"
                placeholder="Cari No Tiket, Nama IGT, KL, atau Catatan..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            
            <div className="history-filter-container">
              <FaFilter className="history-filter-icon" />
              <select 
                className="history-filter-select"
                value={selectedIgt}
                onChange={(e) => setSelectedIgt(e.target.value)}
              >
                <option value="">Semua IGT</option>
                {igtList.map((igt, idx) => (
                  <option key={idx} value={igt.nama_igt}>
                    {igt.nama_igt?.length > 50 ? `${igt.nama_igt.substring(0, 50)}...` : igt.nama_igt}
                  </option>
                ))}
              </select>
            </div>

            <div className="history-filter-container">
              <select 
                className="history-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Semua Status</option>
                {uniqueStatuses.map((status, idx) => (
                  <option key={idx} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {(searchText || selectedIgt || statusFilter) && (
              <button 
                className="history-clear-button"
                onClick={clearFilters}
              >
                <FaTimes /> Reset
              </button>
            )}
          </div>
        </div>

        <div className="history-table-section" ref={tableRef}>
          <div className="history-table-header">
            <h3 className="history-table-title">
              Riwayat Data
              <span className="history-table-badge">{filteredData.length} Record</span>
            </h3>
          </div>

          {error && <div className="history-table-error">{error}</div>}
        
          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            progressComponent={<LoadingSpinner message="Memuat history data..." type="spinner" size="medium" />}
            pagination
            paginationPerPage={20}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            highlightOnHover
            pointerOnHover
            defaultSortFieldId={2}
            defaultSortAsc={false}
            noDataComponent={<p className="text-center text-muted py-4">Tidak ada data history yang tersedia.</p>}
            responsive
            conditionalRowStyles={conditionalRowStyles}
          />
        </div>

        <Tooltip id="nama-tooltip" />
        <Tooltip id="kl-tooltip" />
        <Tooltip id="catatan-tooltip" />
        <Tooltip id="action-tooltip" />

        {searchLoading && (
          <div className="loading-overlay-fullscreen">
            <LoadingSpinner message="Mencari data..." type="dots" size="large" />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default HistoryDataPage;
